import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Common from '../../services/common.js';
import Basic from '../../Components/Basic/Basic';
import Toast from '../../Components/Toast/Toast';

const UploadGeneralDoc = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toastRef = useRef();
  const errorBlockRef = useRef();
  
  // State variables
  const [errors, setErrors] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const [token, setToken] = useState('');
  const [linkCode, setLinkCode] = useState('');
  const [docName, setDocName] = useState('Merchant Document');
  const [docFileError, setDocFileError] = useState('');
  const [form, setForm] = useState({
    doc_file_name: ''
  });
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [localStorageData, setLocalStorageData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const STORAGE_KEY = "caprock-local";

  useEffect(() => {
    // Get URL parameters
    const urlToken = searchParams.get('token');
    const urlLinkCode = searchParams.get('ltoken');
    
    console.log('UploadGeneralDoc: URL parameters:', { urlToken, urlLinkCode });
    
    setToken(urlToken);
    setLinkCode(urlLinkCode);
    
    // Get local storage
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
      setLocalStorageData(stored);
    } catch (error) {
      console.error('Error parsing localStorage:', error);
      setLocalStorageData({});
    }
    
    // Check token - only show alert if token is explicitly empty, not undefined
    if (urlToken === '') {
      tokenAlert(urlToken);
    }
    
    // Get link details if link code exists
    if (urlLinkCode && urlLinkCode !== '') {
      getLinkDetails(urlLinkCode);
    }
    
    // Set document title
    document.title = `${docName} || Caprock Merchant Platform`;
  }, [searchParams, docName]);

  const tokenAlert = (tokenValue) => {
    if (tokenValue === '') {
      alert('This URL is invalid please contact caprock support team');
      window.location.href = 'https://caprock.com.au';
    }
  };

  const getGeneralDocDetails = (docToken) => {
    Common.get_general_doc_details(docToken)
      .then(response => {
        if (typeof response.data !== 'undefined' && response.data.success === 200) {
          const docData = response.data.doc_data;
          setDocName(docData.doc_name);
          document.title = `${docData.doc_name} || Caprock Merchant Platform`;
        }
      })
      .catch(e => {
        console.log(e);
      });
  };

  const getLinkDetails = (linkCode) => {
    Common.get_link_details(linkCode)
      .then(response => {
        if (typeof response.data !== 'undefined' && response.data.success !== 200) {
          alert('Link expired!');
          window.location.href = "https://caprock.com.au";
        } else {
          const sdocName = response.data.link_info['doc_name'] != null 
            ? response.data.link_info['doc_name'] 
            : response.data.link_info['link_name'];
          setDocName(sdocName);
        }
      })
      .catch(e => {
        console.log(e);
      });
  };

  const fetchFile = (event) => {
    const file = event.target.files[0];
    if (event.target.name === 'doc_file') {
      setDocFile(file);
      setForm(prev => ({ ...prev, doc_file_name: file.name }));
      setDocFileError('');
    }
  };

  const submitForm = (e) => {
    e.preventDefault();
    
    if (typeof docFile === 'undefined') {
      setDocFileError('Please select valid document for upload.');
      if (toastRef.current) {
        toastRef.current.miralToast(
          'Warning',
          'Please select valid document for upload.',
          '',
          false
        );
      }
      return false;
    }

    setIsSubmitting(true);
    
    const formData = new FormData();
    formData.append('doc_file', docFile);
    formData.append('token', token);
    formData.append('doc_name', docName);
    formData.append('doc_type', 'requested');
    formData.append('link_code', linkCode);

    const url = Common.base_url() + 'upload-general-document';
    
    fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'Access-Control-Allow-Origin': '*'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success === 200) {
        if (toastRef.current) {
          toastRef.current.miralToast(
            'Success',
            data.message,
            '',
            false
          );
        }
        setTimeout(() => {
          navigate('/sign-in');
        }, 2500);
      } else {
        if (toastRef.current) {
          toastRef.current.miralToast(
            'Warning',
            data.message,
            '',
            false
          );
        }
      }
      setIsSubmitting(false);
    })
    .catch(error => {
      console.error('Upload error:', error);
      setIsSubmitting(false);
    });
  };

  const removeErr = (event) => {
    const element = document.querySelector('.' + event.currentTarget.id + 'err');
    if (element) {
      element.remove();
    }
  };

  const openModal = (id) => {
    setShowModal(true);
  };

  const closeModal = (id) => {
    setShowModal(false);
  };

  return (
    <>
      <Basic />
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
          
          <form method="POST" action="/" className="my-1">
            <div className="flex flex-col space-y-6">
              <div className="w-full max-w-2xl mx-auto">
                <div className="bg-white shadow-lg rounded-lg border border-gray-200 p-6 sm:p-8">
                  <div className="space-y-6">
                    <div className="flex flex-col">
                      <div className="w-full">
                        <p className="text-2xl sm:text-3xl font-bold text-gray-800 tracking-wide leading-normal text-center">
                          Upload your {docName} !
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <input
                        onChange={fetchFile}
                        id="doc_file"
                        type="file"
                        className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        name="doc_file"
                        required
                      />
                      <small className="text-red-500 text-sm">{docFileError}</small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full max-w-md mx-auto">
                <div className="flex flex-col space-y-4">
                 
                  <button
                    id="subBtn"
                    type="button"
                    onClick={submitForm}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Processing ... ' : 'Upload Now'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div
          className="modal fade show d-block"
          id="exampleModal"
          tabIndex="-1"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          style={{ overflowY: 'scroll' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">Please fix this</h5>
                <button type="button" onClick={() => closeModal('exampleModal')} className="btn btn-secondary">
                  Close
                </button>
              </div>
              <div className="modal-body">
                {errors.length > 0 && (
                  <div ref={errorBlockRef} id="errorBlock">
                    {errors.map((error, key) => (
                      <div key={key} className="alert alert-danger">
                        {error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast ref={toastRef} />
    </>
  );
};

export default UploadGeneralDoc;
